import React, { useState, useCallback, useMemo } from 'react';
import { Plus, Edit2, Trash2, ArrowUpDown, CheckSquare, Square, Stethoscope } from 'lucide-react';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusBadge, StatusToggleCell } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import { specializationService } from '../services/api';
import { formatDate } from '../utils/helpers';

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
  },
];
const filterFn = (item, f) => {
  if (f.search && !item.name.toLowerCase().includes(f.search.toLowerCase())) return false;
  if (f.status && item.status !== f.status) return false;
  return true;
};

/* ── Specialization Form ────────────────────────────────────────────────────── */
const SpecializationForm = ({ initial, existingNames, onSubmit, onClose }) => {
  const rules = { name: { required: true } };
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initial ? { name: initial.name, status: initial.status } : { name: '', status: 'active' },
    rules
  );

  const handleFormSubmit = (vals) => {
    const trimmed = vals.name.trim();
    const duplicate = existingNames
      .filter(n => !initial || n.toLowerCase() !== initial.name.toLowerCase())
      .some(n => n.toLowerCase() === trimmed.toLowerCase());
    if (duplicate) { toast.error('Specialization already exists'); return; }
    onSubmit({ ...vals, name: trimmed });
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <FormField label="Specialization Name" error={errors.name} required>
        <input
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          className="input-field"
          placeholder="e.g. Cardiologist, Dermatologist…"
          autoFocus
        />
      </FormField>
      <FormField label="Status">
        <div className="flex gap-3">
          {['active', 'inactive'].map(s => (
            <label key={s} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="status"
                value={s}
                checked={values.status === s}
                onChange={handleChange}
                className="accent-blue-600"
              />
              <StatusBadge status={s} dot={false} />
            </label>
          ))}
        </div>
      </FormField>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">{initial ? 'Update' : 'Add Specialization'}</button>
      </div>
    </Form>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const Specializations = () => {
  const { data: specs, loading, refetch } = useFetch(() => specializationService.getAll());
  const [modal, setModal] = useState(null);   // null | 'add' | 'edit'
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState([]);  // bulk selection ids
  const [sortAZ, setSortAZ] = useState(true);  // true = A→Z, false = Z→A

  const filterFnCb = useCallback(filterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFnCb, specs);

  /* Sort */
  const sorted = useMemo(() => {
    if (!filtered) return [];
    return [...filtered].sort((a, b) => sortAZ ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
  }, [filtered, sortAZ]);

  const existingNames = specs?.map(s => s.name) || [];

  /* ── Handlers ── */
  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        await specializationService.update(selected.id, vals);
        toast.success('Specialization updated!');
      } else {
        const res = await specializationService.create({ name: vals.name });
        toast.success(res?.message || 'Specialization added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch { toast.error('Something went wrong'); }
  };

  const handleDelete = async (id) => {
    const spec = specs?.find(s => s.id === id);
    if (spec?.doctorCount > 0) { toast.error(`Cannot delete — ${spec.doctorCount} doctor(s) assigned`); return; }
    if (!window.confirm('Delete this specialization?')) return;
    try { await specializationService.delete(id); toast.success('Deleted'); refetch(); setChecked(c => c.filter(x => x !== id)); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (spec) => {
    try { await specializationService.toggleStatus(spec.id); toast.success(`Status updated`); refetch(); }
    catch { toast.error('Failed to update'); }
  };

  /* Bulk */
  const allChecked = sorted.length > 0 && sorted.every(s => checked.includes(s.id));
  const toggleAll = () => setChecked(allChecked ? [] : sorted.map(s => s.id));
  const toggleOne = (id) => setChecked(c => c.includes(id) ? c.filter(x => x !== id) : [...c, id]);

  const handleBulk = async (status) => {
    if (!checked.length) return;
    try {
      await specializationService.bulkToggle(checked, status);
      toast.success(`${checked.length} specialization(s) ${status}`);
      refetch(); setChecked([]);
    } catch { toast.error('Bulk update failed'); }
  };

  /* ── Columns ── */
  const columns = [
    {
      key: 'id', label: '', render: (id) => (
        <button onClick={() => toggleOne(id)} className="text-gray-400 hover:text-medihub-primary transition-colors">
          {checked.includes(id) ? <CheckSquare className="w-4 h-4 text-medihub-primary" /> : <Square className="w-4 h-4" />}
        </button>
      )
    },
    {
      key: 'name', label: 'Specialization', render: (v) => (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-medihub-light dark:bg-medihub-dark/40 rounded-lg flex items-center justify-center shrink-0">
            <Stethoscope className="w-4 h-4 text-medihub-primary dark:text-medihub-highlight" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{v}</span>
        </div>
      )
    },
    { key: 'doctorCount', label: 'Doctors Assigned', render: v => (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300">{v}</span>
    )},
    { key: 'createdAt', label: 'Created', render: v => <span className="text-sm text-gray-500">{formatDate(v)}</span> },
    {
      key: 'status', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={v === 'active'} onToggle={() => handleToggle(row)} />
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setSelected(row); setModal('edit'); }}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  /* Stats */
  const activeCount2 = specs?.filter(s => s.status === 'active').length || 0;
  const inactiveCount = specs?.filter(s => s.status === 'inactive').length || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Specializations</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage doctor specialization categories</p>
        </div>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Specialization
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: specs?.length || 0, color: 'text-gray-900 dark:text-white', bg: 'bg-gray-50 dark:bg-gray-800' },
          { label: 'Active', value: activeCount2, color: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
          { label: 'Inactive', value: inactiveCount, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`card p-4 text-center ${bg}`}>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search specialization…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Bulk actions bar */}
      {checked.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-medihub-light dark:bg-medihub-dark/20 border border-medihub-soft dark:border-medihub-dark rounded-xl">
          <span className="text-sm font-medium text-medihub-secondary dark:text-medihub-highlight">{checked.length} selected</span>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => handleBulk('active')} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium">
              Activate All
            </button>
            <button onClick={() => handleBulk('inactive')} className="text-xs px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium">
              Deactivate All
            </button>
            <button onClick={() => setChecked([])} className="text-xs px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Sort + Table */}
      <Table
        title={
          <div className="flex items-center gap-3">
            <span>Specializations ({sorted.length}{activeCount ? ` of ${specs?.length || 0}` : ''})</span>
            <button
              onClick={() => setSortAZ(v => !v)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-medihub-primary dark:hover:text-medihub-highlight transition-colors font-normal"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              {sortAZ ? 'A → Z' : 'Z → A'}
            </button>
            {/* Select-all checkbox in title area */}
            <button onClick={toggleAll} className="flex items-center gap-1 text-xs text-gray-500 hover:text-medihub-primary dark:hover:text-medihub-highlight transition-colors font-normal ml-1">
              {allChecked ? <CheckSquare className="w-3.5 h-3.5 text-medihub-primary" /> : <Square className="w-3.5 h-3.5" />}
              All
            </button>
          </div>
        }
        columns={columns}
        data={sorted}
        loading={loading}
        exportFilename="specializations"
      />

      {/* Add / Edit Modal */}
      <Modal
        isOpen={modal === 'add' || modal === 'edit'}
        onClose={() => { setModal(null); setSelected(null); }}
        title={modal === 'edit' ? 'Edit Specialization' : 'Add Specialization'}
        size="sm"
      >
        <SpecializationForm
          initial={modal === 'edit' ? selected : null}
          existingNames={existingNames}
          onSubmit={handleSave}
          onClose={() => { setModal(null); setSelected(null); }}
        />
      </Modal>
    </div>
  );
};

export default Specializations;
